import {
	Component, ElementRef, EventEmitter, Input, OnInit, Output, Renderer2, Self, forwardRef
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { PaginationConfig } from './pagination.config';
import { TranslatePipe } from '../../../translate/translate.pipe';

import { SharedService } from '../../services/shared.service';

export interface PageChangedEvent {
	itemsPerPage: number;
	page: number;
}
export const PAGINATION_CONTROL_VALUE_ACCESSOR: any = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => PaginationComponent),
	multi: true
};

@Component({
	selector: 'pagination',
	template: require('./pagination.component.html'),
	styles: [require('./pagination.component.less')],
	providers: [PAGINATION_CONTROL_VALUE_ACCESSOR, TranslatePipe]
})
export class PaginationComponent implements ControlValueAccessor, OnInit {
	nextDisable: boolean;
	public config: any;
	changed: boolean;
	/** if `true` aligns each link to the sides of pager */
	@Input() public align: boolean;
	/** limit number for page links in pager */
	@Input() public maxSize = 3;
	/** if false first and last buttons will be hidden */
	@Input() public boundaryLinks: boolean;
	/** if false previous and next buttons will be hidden */
	@Input() public directionLinks: boolean;
	// labels
	/** first button text */
	@Input() public firstText: string;
	/** previous button text */
	@Input() public previousText: string;
	/** next button text */
	@Input() public nextText: string;
	/** last button text */
	@Input() public lastText: string;
	/** if true current page will in the middle of pages list */
	@Input() public rotate: boolean;
	// css
	/** add class to <li> */
	@Input() public pageBtnClass: string;

	/** if true pagination component will be disabled */
	@Input() public disabled: boolean;

	/** fired when total pages count changes, $event:number equals to total pages count */
	@Output() public numPages: EventEmitter<number> = new EventEmitter<number>();
	/** fired when page was changed, $event:{page, itemsPerPage} equals to object with current page index and number of items per page */
	@Output() public pageChanged: EventEmitter<PageChangedEvent> = new EventEmitter<PageChangedEvent>();

	/** maximum number of items per page. If value less than 1 will display all items on one page */
	@Input('selectedItemsPerPage') _itemsPerPage: number;
	@Input('changed') _changed: boolean;
	@Input()
	public get itemsPerPage(): number {
		return this._itemsPerPage;
	}
	public set itemsPerPage(v: number) {
		this._itemsPerPage = v;
		this.totalPages = this.calculateTotalPages();
	}
	//  @Output() alertEvent: EventEmitter<any> = new EventEmitter<any>();
	@Output() paginationComponentEvent: EventEmitter<any> = new EventEmitter<any>();
	/**
	 * total number of items in all pages
	 */
	@Input()
	public get totalItems(): number {
		return this._totalItems;
	}

	/**
	 * To detect change in items per page
	 */
	@Input()
	public get changedValue(): boolean {
		return this._changedValue;
	}

	/**
	 * Returns the changed Value
	 * @param  {boolean} v
	 */
	public set changedValue(v: boolean) {
		this._changedValue = v;
	}

	/**
	 * Function to call base class
	 * @param  {number} v
	 */
	public set totalItems(v: number) {
		this._totalItems = v;
		this.totalPages = this.calculateTotalPages();
	}

	/**
	 * returns totalPages
	 * @returns number
	 */
	public get totalPages(): number {
		return this._totalPages;
	}

	/**
	 * Sets Total pages
	 * @param  {number} v
	 */
	public set totalPages(v: number) {
		this._totalPages = v;
		this.numPages.emit(v);
		if (this.inited) {
			this.selectPage(this.page);
		}
	}

	/**
	 * Calls pageChanged event
	 * @param  {number} value
	 */
	public set page(value: number) {
		const _previous = this._page;
		this._page = (value > this.totalPages) ? this.totalPages : (value || 1);
		if (_previous === this._page || typeof _previous === 'undefined') {
			return;
		}
		this.pageChanged.emit({
			page: this._page,
			itemsPerPage: this.itemsPerPage,
		});
	}

	public get page(): number {
		return this._page;
	}

	public onChange: any = Function.prototype;
	public onTouched: any = Function.prototype;
	public renderer: Renderer2;
	public elementRef: ElementRef;
	public classMap: string;
	public pages: any[];
	protected _totalItems: number;
	protected _totalPages: number;
	protected inited: boolean = false;
	protected _page: number = 1;
	_changedValue: boolean = false;
	offsetValue: boolean;
	offsetValuePage: boolean;
	changeValue: boolean;
	reqOffset: string;
	passedItems: string;
	paginationObject: any = [];

	public constructor(renderer: Renderer2, elementRef: ElementRef, paginationConfig: PaginationConfig,
		private _sharedservice: SharedService, private _translatePipe: TranslatePipe) {
		this.renderer = renderer;
		this.elementRef = elementRef;
		if (!this.config) {
			this.configureOptions(paginationConfig.main);
		}
	}

	public configureOptions(config: any): void {
		this.config = Object.assign({}, config);
	}

	public ngOnInit(): void {
		this.classMap = this.elementRef.nativeElement.getAttribute('class') || '';
		// watch for maxSize
		this.maxSize = typeof this.maxSize !== 'undefined'
			? this.maxSize
			: this.config.maxSize;
		this.rotate = typeof this.rotate !== 'undefined'
			? this.rotate
			: this.config.rotate;
		this.boundaryLinks = typeof this.boundaryLinks !== 'undefined'
			? this.boundaryLinks
			: this.config.boundaryLinks;
		this.directionLinks = typeof this.directionLinks !== 'undefined'
			? this.directionLinks
			: this.config.directionLinks;
		this.pageBtnClass = typeof this.pageBtnClass !== 'undefined'
			? this.pageBtnClass
			: this.config.pageBtnClass;

		// base class
		this.itemsPerPage = typeof this.itemsPerPage !== 'undefined'
			? this.itemsPerPage
			: this.config.itemsPerPage;
		this.totalPages = this.calculateTotalPages();

		// this class
		this.pages = this.getPages(this.page, this.totalPages);
		this.inited = true;
	}

	/**
	 * Function to write value to a page
	 * @param  {number} value
	 * @returns void
	 */
	public writeValue(value: number): void {
		this.page = value;
		this.pages = this.getPages(this.page, this.totalPages);
	}

	public getText(key: string): string {
		// return (this as any)[key + 'Text'] || this.config[key + 'Text'];
		return this._translatePipe.transform(key, null);
	}

	public noPrevious(): boolean {
		return this.page === 1;
	}

	public noNext(): boolean {
		return this.page === this.totalPages;
	}

	public registerOnChange(fn: (_: any) => {}): void {
		this.onChange = fn;
	}

	public registerOnTouched(fn: () => {}): void {
		this.onTouched = fn;
	}

	/**
	 * Function to set selected page
	 * @param  {number} page
	 * @param  {Event} event?
	 * @returns void
	 */
	public selectPage(page: number, event?: Event): void {
		this.offsetValue = false;
		this.offsetValuePage = false;
		if (this._changedValue === true) {
			page = 1;
			this.offsetValue = true;
			this.eventEmitter(this.itemsPerPage, 0, event)
			this._changedValue = false;
		}
		else {
			if (page > this.totalPages) {
				let offset = this.itemsPerPage * (page - 2);
				this.eventEmitter(this.itemsPerPage, offset, event)
			}
			else {
				let offset = this.itemsPerPage * (page - 1);
				this.paginationObject = {
					itemsPerPage: this.itemsPerPage,
					offset: offset,
					isClicked: (event) ? true : false
				}
				this.paginationComponentEvent.emit(this.paginationObject);
			}
		}
		if (event) {
			event.preventDefault();
		}
		if (!this.disabled) {
			if (event && event.target) {
				let target: any = event.target;
				target.blur();
			}
			this.writeValue(page);
			this.onChange(this.page);
		}
	}

	eventEmitter(passedItems: any, reqOffset: any, event?: any) {
		this.paginationObject = {
			passedItems: passedItems,
			reqOffset: reqOffset,
			isClicked: (event) ? true : false
		}
	}

	emitterEvent() {
		return this.paginationObject;

	}

	/**
	 * Create page object used in template
	 * @param  {number} num
	 * @param  {string} text
	 * @param  {boolean} active
	 * @returns boolean
	 */
	protected makePage(num: number, text: string, active: boolean): { number: number, text: string, active: boolean } {
		return { text, number: num, active };
	}

	/**
	 * Get Page Object used in template
	 * @param  {number} currentPage
	 * @param  {number} totalPages
	 * @returns any
	 */
	protected getPages(currentPage: number, totalPages: number): any[] {
		let pages: any[] = [];
		// Default page limits
		let startPage = 1;
		let endPage = totalPages;
		let isMaxSized = typeof this.maxSize !== 'undefined' && this.maxSize < totalPages;
		// recompute if maxSize
		if (isMaxSized) {
			if (this.rotate) {
				// Current page is displayed in the middle of the visible ones
				startPage = Math.max(currentPage - Math.floor(this.maxSize / 2), 1);
				endPage = startPage + this.maxSize - 1;

				// Adjust if limit is exceeded
				if (endPage > totalPages) {
					endPage = totalPages;
					startPage = endPage - this.maxSize + 1;
				}
			} else {
				// Visible pages are paginated with maxSize
				startPage = ((Math.ceil(currentPage / this.maxSize) - 1) * this.maxSize) + 1;
				// Adjust last page if limit is exceeded
				endPage = Math.min(startPage + this.maxSize - 1, totalPages);
			}
		}

		// Add page number links
		for (let num = startPage; num <= endPage; num++) {
			if (this.offsetValue === true) { currentPage = 1 }
			let page = this.makePage(num, num.toString(), num === currentPage);
			pages.push(page);
		}
		
		// Add links to move between page sets
		if (isMaxSized && !this.rotate) {
			pages = this.movePageSets(startPage, endPage, totalPages, pages);
		}
		return pages;
	}

	movePageSets(startPage: any, endPage:any, totalPages:any, pages: any) {
		if (startPage > 1) {
			let previousPageSet = this.makePage(startPage - 1, '...', false);
			pages.unshift(previousPageSet);
		}
		if (endPage < totalPages) {
			let nextPageSet = this.makePage(endPage + 1, '...', false);
			pages.push(nextPageSet);
		}

		return pages;
	}

	/**
	 * Base Class
	 * @returns number
	 */
	public calculateTotalPages(): number {
		let totalPages = this.itemsPerPage < 1
			? 1
			: Math.ceil(this.totalItems / this.itemsPerPage);
		return Math.max(totalPages || 0, 1);
	}
}